/* Header rules and sub-category photos share the mapping in Navigation filters. */
(() => {
  if (window.loulouNavigationFilters) return;
  const normalize = value => String(value || '').trim().toLowerCase();
  const pathParts = value => String(value || '').split('>').map(normalize);
  const labels = value => [...new Set(String(value || '').split(',').map(value => value.trim()).filter(Boolean))];
  const descendants = (scope, selector) => [
    ...(scope.matches?.(selector) ? [scope] : []), ...scope.querySelectorAll(selector),
  ];

  function configuration() {
    const template = document.querySelector('template[data-navigation-filters]');
    if (!template) return null;
    try { return JSON.parse(template.content.textContent); } catch (_) { return null; }
  }

  function assignments(config) {
    const seen = new Set();
    return (config?.rules || []).flatMap(rule => {
      const parts = pathParts(rule.path);
      const key = parts.join('>');
      if (parts.some(part => !part) || seen.has(key)) return [];
      seen.add(key);
      return [{ ...rule, parts }];
    });
  }

  function resolve(path, rules) {
    const parts = pathParts(path);
    const matches = rules.filter(rule => rule.parts.every((part, index) => parts[index] === part)
      && (rule.parts.length === parts.length || (rule.children && rule.parts.length < parts.length)))
      .sort((a, b) => a.parts.length - b.parts.length);
    let result = { behavior: 'preserve', age: 'inherit', gender: 'inherit', availability: 'inherit' };
    for (const rule of matches) {
      if (rule.behavior === 'preserve') {
        // A bypass ends inheritance. A closer explicit assignment may start again.
        result = { behavior: 'preserve', age: 'inherit', gender: 'inherit', availability: 'inherit' };
      } else {
        result.behavior = 'apply';
        for (const key of ['age', 'gender', 'availability']) {
          if (rule[key] && rule[key] !== 'inherit') result[key] = rule[key];
        }
      }
    }
    return result;
  }

  function destination(original, rule, config) {
    if (!config || rule.behavior !== 'apply') return original;
    let url;
    try { url = new URL(original, location.origin); } catch (_) { return original; }
    // Never modify external sites, pages, products, collection indexes or anchors.
    if (url.origin !== location.origin || !/^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?collections\/[^/]+\/?$/i.test(url.pathname)) return original;
    const sizeParameter = config.sizeParameter;
    const genderParameter = config.genderParameter;
    let sizes;
    if (sizeParameter && rule.age !== 'inherit') {
      if (rule.age === 'none') sizes = [];
      else if (Object.hasOwn(config.mapping || {}, rule.age)) sizes = labels(config.mapping[rule.age]);
      if (sizes) url.searchParams.delete(sizeParameter);
    }
    if (genderParameter && rule.gender !== 'inherit') {
      url.searchParams.delete(genderParameter);
      if (['Boy', 'Girl', 'Neutral'].includes(rule.gender)) url.searchParams.set(genderParameter, rule.gender);
    }
    if (rule.availability !== 'inherit') {
      url.searchParams.delete('filter.v.availability');
      if (rule.availability === 'in_stock') url.searchParams.set('filter.v.availability', '1');
    }
    url.searchParams.delete('page');
    // Shopify requires literal separators, with each individual label encoded.
    const query = [url.searchParams.toString(), sizes?.length
      ? `${encodeURIComponent(sizeParameter)}=${sizes.map(encodeURIComponent).join(',')}` : ''].filter(Boolean).join('&');
    return url.pathname + (query ? `?${query}` : '') + url.hash;
  }

  function imageAge(selected, mapping) {
    const chosen = new Set(selected.map(normalize));
    let winner = '', highest = 0;
    for (const age of ['baby', 'toddler', 'kid']) {
      const members = new Set(labels(mapping?.[age]).map(normalize));
      const votes = [...chosen].filter(label => members.has(label)).length;
      if (votes > 0 && votes >= highest) { winner = age; highest = votes; }
    }
    return winner;
  }

  function updateImages(scope = document) {
    const config = configuration();
    descendants(scope, '[data-collection-subcategories]').forEach(root => {
      let active;
      try { active = JSON.parse(root.dataset.activeFilterLabels || '[]'); } catch (_) { return; }
      const selected = active.find(filter => filter.parameter === config?.sizeParameter)?.labels || [];
      const override = normalize(root.dataset.ageOverride);
      const age = ['baby', 'toddler', 'kid'].includes(override) ? override
        : imageAge(selected, config?.mapping) || root.dataset.selectedAge;
      root.querySelectorAll('[data-image-options]').forEach(tile => {
        let options;
        try { options = JSON.parse(tile.dataset.imageOptions); } catch (_) { return; }
        const defaultAge = tile.dataset.defaultAge, defaultGender = tile.dataset.defaultGender;
        const resolvedAge = age || defaultAge, gender = root.dataset.selectedGender || defaultGender;
        const context = `${resolvedAge}_${gender}_image`;
        const keys = [context, `${resolvedAge}_${defaultGender}_image`, `${defaultAge}_${gender}_image`, `${defaultAge}_${defaultGender}_image`, 'default_image'];
        const key = keys.find(key => options[key]);
        const image = tile.querySelector('img');
        if (!key || !image) return;
        if (tile.dataset.imageSource !== key) {
          image.removeAttribute('srcset');
          image.removeAttribute('sizes');
          image.src = options[key];
        }
        tile.dataset.imageContext = context;
        tile.dataset.imageSource = key;
      });
    });
  }

  function refresh() {
    const config = configuration();
    const rules = assignments(config);
    let changed = 0;
    document.querySelectorAll('[data-navigation-menu] a[data-navigation-path]').forEach(link => {
      if (!link.hasAttribute('data-navigation-original-href')) link.dataset.navigationOriginalHref = link.getAttribute('href');
      const original = link.dataset.navigationOriginalHref;
      const menu = link.closest('[data-navigation-menu]').dataset.navigationMenu;
      const next = config?.menu && menu === config.menu
        ? destination(original, resolve(link.dataset.navigationPath, rules), config) : original;
      link.setAttribute('href', next);
      if (next !== original) changed++;
    });
    updateImages();
    const status = document.querySelector('[data-navigation-filter-status]');
    if (status) {
      const menus = [...document.querySelectorAll('[data-navigation-menu]')].filter(menu => menu.dataset.navigationMenu === config?.menu);
      const paths = new Set(menus.flatMap(menu => [...menu.querySelectorAll('[data-navigation-path]')].map(node => pathParts(node.dataset.navigationPath).join('>'))));
      const missing = rules.filter(rule => !paths.has(rule.parts.join('>'))).map(rule => rule.path);
      const duplicateCount = (config?.rules || []).length - rules.length;
      status.textContent = [
        `${changed} rendered links updated (desktop and mobile counted separately).`,
        !menus.length ? 'Select the same menu as Header.' : '',
        missing.length ? `Unmatched assignments: ${missing.join('; ')}. Check renamed or moved items.` : '',
        duplicateCount ? `${duplicateCount} duplicate or blank assignments ignored. First matching path wins.` : '',
      ].filter(Boolean).join(' ');
    }
  }

  window.loulouNavigationFilters = { refresh, updateImages, resolve, destination, imageAge };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', refresh, { once: true });
  else refresh();
  document.addEventListener('shopify:section:load', refresh);
  // Shopify removes the old section after emitting unload.
  document.addEventListener('shopify:section:unload', () => setTimeout(refresh, 0));
  document.addEventListener('shopify:section:reorder', refresh);
})();
